import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useI18n } from "i18n";
import { Link } from "react-router-dom";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { IconButton } from "@remo-co/ui-core/src/components/IconButton";
import { Alert } from "@remo-co/ui-core/src/components/Alert";
import { Info } from "@remo-co/ui-core/src/icons/Info";
import { Close } from "@remo-co/ui-core/src/icons/Close";
import logger from "logging/logger";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import useSystemClock from "modules/system/hooks/useSystemClock";
import { selectCurrentEvent } from "modules/event/selectors";
import EventCountdownTimer from "modules/theater/banner/userPresence/EventCountdownTimer";
import { selectIsCompanyOwner } from "modules/auth/redux/selectors";
import { useStyles } from "./styles";

/**
 * This component handles free plan usage alert
 * Show a plan upgrade alert to company manager and company owner 5 mins before event end time
 * Only company Owner can see upgrade plan button.
 * For managers shows alert message to ask admin to upgrade plan.
 */

const ONE_MIN_MS = 60000;
const FREE_PLAN_ALERT_TIME = 5 * ONE_MIN_MS;

const FreePlanUsageAlert = () => {
  const { t } = useI18n(["event", "company", "settings"]);
  const event = useSelector(selectCurrentEvent);
  const { getCurrentTime } = useSystemClock();
  const [showAlert, setAlert] = useState(false);
  const { myCompanyPlan, isFreePlan, getTimeLimitPerEvent } =
    useCompanyPlanSettings();
  const isCompanyOwner = useSelector(selectIsCompanyOwner);
  const maxTimeForEvent = getTimeLimitPerEvent();
  const isCompanyFreePlan = isFreePlan(myCompanyPlan);
  const classes = useStyles();

  const alertTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const alertText = isCompanyOwner
    ? t("company:free.plan.expired.owner")
    : t("company:free.plan.expired.message");

  const setAlertTimerRef = (fn: () => void, ms: number) => {
    if (alertTimer.current) clearTimeout(alertTimer.current);

    alertTimer.current = setTimeout(fn, ms);
  };

  // clean up alert timer when unmounting
  useEffect(
    () => () => {
      if (alertTimer.current) clearTimeout(alertTimer.current);
    },
    [],
  );

  /**
   * Checks if end time is > than 5mins, then set a timer to show free usage alert at last 5th min
   */
  const checkEndTimeForShowingAlert = async () => {
    if (!event || !event.endTime || !event.startTime) return;

    logger.info(
      `[FreePlanUsageAlert][checkEndTimeForShowingAlert] Checking end time for Event ${event?.id}.`,
    );

    const currentEventDuration = event.endTime - event.startTime;

    // Calculate alert showing time from plan max event time limit
    const showNotificationAt =
      maxTimeForEvent * ONE_MIN_MS - FREE_PLAN_ALERT_TIME;

    // Check if event duration is less than free usage alert showing time then we don't need to set alert timer
    if (currentEventDuration && currentEventDuration < showNotificationAt) {
      logger.info(
        `[FreePlanUsageAlert][checkEndTimeForShowingAlert] cannot set alert timer, because currentEventDuration: ${
          currentEventDuration / ONE_MIN_MS
        } is less than show alert time limit ${
          showNotificationAt / ONE_MIN_MS
        }`,
      );

      return;
    }

    const serverTime = getCurrentTime();
    const timeRemaining = event.endTime - serverTime;
    // Calculate left alert time if current event duration is greater than showNotificationAt time
    const leftAlertTime = currentEventDuration - showNotificationAt;

    logger.info(
      `[FreePlanUsageAlert][checkEndTimeForShowingAlert] ${event.id} timeRemaining: ${timeRemaining}
      endTime: ${event?.endTime} cl: ${serverTime} leftAlertTime: ${leftAlertTime}`,
    );

    if (timeRemaining > 0) {
      // Calculate when to show alert based on event end time and calculated client time
      const showAlertAt = event.endTime - leftAlertTime - serverTime;

      // Clear alert timer
      if (showAlertAt > 0) {
        setAlertTimerRef(() => {
          logger.info(
            `[FreePlanUsageAlert][checkEndTimeForShowingAlert] Event ${event.id} show free usage alert in : ${showAlertAt}`,
          );
          setAlert(true);
        }, showAlertAt);
      } else {
        logger.info(
          `[FreePlanUsageAlert][checkEndTimeForShowingAlert] Event ${event.id} show free usage alert in : ${showAlertAt}`,
        );
        setAlert(true);
      }
    }
  };

  // display free plan usage alert before 5mins left in event ending for free plan.
  useEffect(() => {
    if (isCompanyFreePlan) {
      checkEndTimeForShowingAlert();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.endTime]);

  /**
   * Check if company plan is not free plan and show alert is false
   * Show alert true before 5mins of maxTimeForEvent of plan
   */
  if (!isCompanyFreePlan || !showAlert) {
    return null;
  }

  return (
    <Alert
      data-testid="free-usage-alert"
      action={
        <IconButton
          aria-label="close"
          className={classes.closeBtn}
          color="inherit"
          size="small"
          onClick={() => {
            setAlert(false);
          }}
        >
          <Close fontSize="inherit" />
        </IconButton>
      }
      icon={<Info className={classes.infoBtn} fontSize="inherit" />}
      classes={{ root: classes.alertBox, message: classes.alertMessage }}
    >
      {t("event:event.ending")} <EventCountdownTimer showTimer={setAlert} /> |{" "}
      {alertText}
      {/* check if is company owner then only display upgrade plan button else dont */}
      {isCompanyOwner && (
        <Button<typeof Link>
          variant="primary"
          color="blue"
          size="md"
          data-testid="upgrade-plan-btn"
          className={classes.upgradeBtn}
          to="/account-settings/billing"
          component={Link}
        >
          {t("settings:plan.upgrade")}
        </Button>
      )}
    </Alert>
  );
};

export default FreePlanUsageAlert;
